'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ListPageLayout } from '@terra/ui-design-system';
import type { MergeAssetsPublicBodyDto } from '@terra/shared-contracts-applications';
import { POST_BATCH_URL_PARAMETRIZATION_ASSET_DEDUP_FLAG } from '@terra/shared-contracts-feature-flags';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { EmptyState } from '../EmptyState';
import { Database01Icon } from '@terra/ui-icons';
import { AssetsBulkActionsBar } from './AssetsBulkActionsBar';
import { AssetMergeConfirmDialog } from '../dialogs/AssetMergeConfirmDialog';
import { TorchDiscoveryBanner } from './TorchDiscoveryBanner';
import { useAssetsWithFilters } from '../../hooks/useAssetsWithFilters';
import { useAssetsVisitBaseline } from '../../hooks/useAssetsVisitBaseline';
import { useTabScopedDomainTree } from '../../store/tab/domainTreeTabScopedFilterStore';
import {
  ASSET_TYPE,
  useTabScopedAssetsFilters,
  type AssetType,
  type AssetsView,
} from '../../store/tab/assetsTabScopedFilterStore';
import { useDiscoveryStatus } from '../../app/providers/DiscoveryStatusProvider';
import { useInvalidateDiscoveryAssets } from '../../hooks/useAssetsDiscoveryRefresh';
import { useAssetMergeEnabled } from '../../hooks/useAssetMergeEnabled';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  resolveTorchAssetsBannerPhase,
  TORCH_ASSETS_BANNER_PHASE,
} from '../../utils/torch-assets-banner-phase';
import { useMergeAssets } from '../../hooks/api/useMergeAssets';
import {
  isAssetMergeCandidate,
  isAssetMergeChromeVisible,
} from '../../utils/asset-merge/asset-merge-selection';
import { useSourceCodeInfo } from '../../hooks/dashboard/useSourceCodeInfo';
import { AssetsTable } from './assets-table/AssetsTable';
import { AssetsTableContextProvider } from './assets-table/AssetsTableContext';
import { WebpageScreenshotDialog } from './assets-table/WebpageScreenshotDialog';
import {
  AssetsViewTabs,
  DEFAULT_ASSETS_VIEWS,
} from './assets-header/AssetsViewTabs';
import { AssetsContextNavigation } from './assets-header/AssetsContextNavigation';
import { AssetsViewOptionsMenu } from './assets-header/AssetsViewOptionsMenu';
import { AssetsFilterPanel } from './assets-header/AssetsFilterPanel';
import { useAssetsTableView } from './assets-table/useAssetsTableView';
import { useAssetsTableModel } from './assets-table/useAssetsTableModel';

const RELEVANT_PROCESSING_ASSET_TYPES: AssetType[] = [
  ASSET_TYPE.ENDPOINTS,
  ASSET_TYPE.WEB_ENDPOINTS,
  ASSET_TYPE.WEB_PAGES,
  ASSET_TYPE.ALL,
];

type AssetsTabWrapperProps = {
  header?: ReactNode;
  views?: readonly AssetsView[];
  tabs?: ReactNode;
};

export function AssetsTabWrapper({
  header,
  views = DEFAULT_ASSETS_VIEWS,
  tabs,
}: AssetsTabWrapperProps) {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const { data: sourceCodeInfo } = useSourceCodeInfo(targetId ?? '');
  const showWithCodeGraphFilter = sourceCodeInfo?.isConnected === true;
  const isSourceCodeDisconnected = sourceCodeInfo?.isConnected === false;
  const { selectedNodes } = useTabScopedDomainTree();
  const lastVisitTimestamp = useAssetsVisitBaseline({
    targetId,
    targetType,
  });
  const { isDiscoveryActive, isParametrizationActive, isDeduplicationActive } =
    useDiscoveryStatus();
  const isAssetMergeEnabled = useAssetMergeEnabled();
  const { isFeatureFlagEnabled: isPostParamDedupEnabled } = useFeatureFlags(
    POST_BATCH_URL_PARAMETRIZATION_ASSET_DEDUP_FLAG,
  );
  const { mutateAsync: mergeAssets, isPending: isMerging } = useMergeAssets();
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const {
    typeFilter: storedTypeFilter,
    verified: storedVerified,
    view: storedView,
    setView,
  } = useTabScopedAssetsFilters();
  const fallbackView = views[0];

  useEffect(() => {
    if (fallbackView && !views.includes(storedView)) {
      setView(fallbackView);
    }
  }, [fallbackView, setView, storedView, views]);
  const tableView = useAssetsTableView({
    typeFilter: storedTypeFilter,
    verified: storedVerified,
  });
  const {
    assets,
    groups,
    totalCount,
    isInitialLoading,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    requestFilters,
    searchTerm,
    typeFilter,
    securityFilter,
    detectionView,
    apiTypes,
    verified,
    withCodeGraph,
    setWithCodeGraph,
  } = useAssetsWithFilters({
    selectedNodes,
    orderBy: tableView.orderBy,
    groupBy: tableView.groupBy,
  });

  useEffect(() => {
    if (isSourceCodeDisconnected && withCodeGraph) {
      setWithCodeGraph(false);
    }
  }, [isSourceCodeDisconnected, setWithCodeGraph, withCodeGraph]);

  const isMergeVisible = isAssetMergeChromeVisible({
    isEnabled: isAssetMergeEnabled,
    typeFilter,
    verified,
  });
  const tableModel = useAssetsTableModel({
    view: tableView,
    assets,
    groups,
    applicationId: targetId,
    lastVisitTimestamp,
    selectionScopeKey: `${typeFilter}:${securityFilter ?? ''}:${apiTypes.join(',')}:${verified}:${searchTerm}:${selectedNodes.join(',')}`,
  });
  const { selectedAssets, clearSelection } = tableModel.selection;
  const mergeCandidates = selectedAssets.filter(isAssetMergeCandidate);
  const hasKnownAssets = assets.length > 0 || totalCount > 0;
  const isParametrizationActiveForTab =
    isParametrizationActive &&
    RELEVANT_PROCESSING_ASSET_TYPES.includes(typeFilter);
  const bannerPhase = resolveTorchAssetsBannerPhase({
    isDiscoveryActive,
    isParametrizationActive: isParametrizationActiveForTab,
    isDeduplicationActive,
    isDeduplicationEnabled: isPostParamDedupEnabled,
    assetTypeFilter: typeFilter,
    relevantProcessingAssetTypes: RELEVANT_PROCESSING_ASSET_TYPES,
  });
  const showDiscoveryBanner = hasKnownAssets && bannerPhase !== null;
  const invalidateDiscoveryAssets = useInvalidateDiscoveryAssets();
  const handleRefresh = useCallback(async () => {
    if (!targetId) {
      await refetch();

      return;
    }

    await invalidateDiscoveryAssets({ applicationId: targetId });
  }, [invalidateDiscoveryAssets, refetch, targetId]);
  const handleLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const handleConfirmMerge = useCallback(
    async (body: MergeAssetsPublicBodyDto) => {
      if (!targetId) {
        return;
      }

      await mergeAssets({ applicationId: targetId, body });
      clearSelection();
    },
    [clearSelection, mergeAssets, targetId],
  );

  if (!targetId) {
    return (
      <EmptyState
        icon={Database01Icon}
        title="No Target Selected"
        description="Please select a company and application to view assets."
      />
    );
  }

  return (
    <AssetsTableContextProvider value={tableModel.contextValue}>
      <ListPageLayout
        header={header}
        tabs={tabs ?? <AssetsViewTabs views={views} />}
        navigation={
          <AssetsContextNavigation
            requestFilters={requestFilters}
            onRefresh={handleRefresh}
            isLoading={isInitialLoading || isRefetching}
            optionsMenu={
              <AssetsViewOptionsMenu
                groupByOptions={tableView.groupByOptions}
                groupBy={tableView.groupBy}
                onGroupByChange={tableView.setGroupBy}
                areAllGroupsCollapsed={tableModel.areAllGroupsCollapsed}
                onExpandAllGroups={tableModel.expandAllGroups}
                onCollapseAllGroups={tableModel.collapseAllGroups}
                applicationId={targetId}
                exportFilters={requestFilters}
              />
            }
          />
        }
        filters={
          <AssetsFilterPanel
            filteredCount={totalCount}
            showWithCodeGraphFilter={showWithCodeGraphFilter}
            selection={
              isMergeVisible && selectedAssets.length > 0 ? (
                <AssetsBulkActionsBar
                  selectedCount={selectedAssets.length}
                  mergeCandidates={mergeCandidates}
                  isLoading={isInitialLoading || isRefetching || isMerging}
                  onClearSelection={clearSelection}
                  onMerge={() => setIsMergeDialogOpen(true)}
                />
              ) : null
            }
          />
        }
      >
        {showDiscoveryBanner ? (
          <TorchDiscoveryBanner
            isDiscoveryActive={isDiscoveryActive}
            isProcessingActive={isParametrizationActiveForTab}
            isDeduplicationActive={isDeduplicationActive}
            isDeduplicationEnabled={isPostParamDedupEnabled}
            assetTypeFilter={typeFilter}
            relevantProcessingAssetTypes={RELEVANT_PROCESSING_ASSET_TYPES}
          />
        ) : null}

        <AssetsTable
          kind={tableView.kind}
          rows={tableModel.rows}
          loadMoreRowIndex={tableModel.loadMoreRowIndex}
          assetCount={assets.length}
          groupBy={tableView.groupBy}
          detectionView={detectionView}
          sort={tableView.sort}
          isLoading={isInitialLoading}
          isRefetching={isRefetching}
          isFetchingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          hasKnownAssets={hasKnownAssets}
          processing={{
            isDiscoveryActive,
            isParametrizationActive: isParametrizationActiveForTab,
            isDeduplicationActive:
              bannerPhase === TORCH_ASSETS_BANNER_PHASE.deduplication,
          }}
          onLoadMore={handleLoadMore}
          onSortChange={tableView.setSort}
          onOpenAsset={tableModel.openAsset}
          onOpenAssetContextMenu={tableModel.openAssetContextMenu}
        />

        <WebpageScreenshotDialog
          request={tableModel.screenshotRequest}
          onClose={tableModel.closeScreenshots}
        />

        {isMergeVisible ? (
          <AssetMergeConfirmDialog
            isOpen={isMergeDialogOpen}
            assets={mergeCandidates}
            isSubmitting={isMerging}
            onOpenChange={setIsMergeDialogOpen}
            onConfirm={handleConfirmMerge}
          />
        ) : null}
      </ListPageLayout>
    </AssetsTableContextProvider>
  );
}
